import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  History,
  Maximize2,
  MessageSquare,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import { groupChats, recentChats } from '../chatGroups';
import { usePromptSuggestions } from '../promptSuggestions';
import { useSoteriaChat } from '../hooks/useSoteriaChat';
import { useSoteriaSidePanelStore } from '../stores/sidePanelStore';
import { useAttachmentUploads } from '../hooks/useAttachmentUploads';
import { SoteriaComposer } from './SoteriaComposer';
import { SoteriaMessageList } from './SoteriaMessageList';
import { ScrollToBottom } from './ScrollToBottom';
import { cn } from '@/lib/utils';
import { useUserStore } from '@/entities/user/store';

/**
 * How wide the assistant's column is.
 *
 * IT USED TO BE HALF OF A PAIR. This docblock read "EXPORTED so AuthLayout can
 * pin the same value as a margin-right on `<main>` — that's how we push the
 * page instead of overlaying it", and a matching `SOTERIA_PANEL_MARGIN_CLASS`
 * sat beside it. Two constants that had to agree, forever, for the page to be
 * pushed rather than covered — and a rule nothing enforces is a rule that is
 * not kept.
 *
 * The panel is a flex sibling of the page now. It takes this width out of the
 * row, the page column takes what is left, and there is no second value to
 * keep in step. That is why the margin constant is gone rather than moved.
 *
 *   - `clamp(420px, 32vw, 540px)` reads comfortably on a 1440px laptop
 *     (~460px), scales up to 540 on a wide monitor, floors at 420 on
 *     smaller screens.
 *   - The outer min(…, 92vw) keeps it from taking the whole of a narrow one.
 */
/**
 * BOTH ARE WRITTEN OUT IN FULL, and that is not an oversight.
 *
 * Tailwind scans SOURCE TEXT for complete class names. A class assembled at
 * runtime — `` `w-[${WIDTH}]` `` — is never seen by the scanner, so the CSS is
 * never generated and the class is inert. The first version of this pair did
 * exactly that in the name of having one constant, and it did not merely fail
 * to fix the bug it was for: it took the panel's own width down with it,
 * because `SOTERIA_PANEL_WIDTH_CLASS` had been a literal until then.
 *
 * So the width appears twice, deliberately, and `SoteriaSidePanel.test.tsx`
 * asserts the two carry the same value. A test is the thing that keeps them in
 * step here; interpolation cannot, whatever it looks like.
 */
export const SOTERIA_PANEL_WIDTH_CLASS =
  'w-[min(clamp(420px,32vw,540px),92vw)]';

/**
 * The same width as a RIGHT offset, for a page that is `fixed` rather than a
 * flex sibling of this panel.
 *
 * THE COMMENT BELOW WARNS AGAINST EXACTLY THIS SHAPE, and it is right about
 * the reason: a `fixed` page plus a matching margin is two constants that have
 * to agree, and they did not. The answer it reached was to put the panel in
 * the flow, which is what every page that shares the row gets for free.
 *
 * `ClientDetailPage` and the backtest `Report` are not in that row. Each is
 * `fixed inset-0 top-14` — a takeover, out of flow, reaching `right: 0`
 * whatever the row does — so the panel opened UNDERNEATH it and the Sparkles
 * button appeared to do nothing there. Those pages should join the row; this
 * is the interim, and the width above says why the value is repeated rather
 * than interpolated. `pagesMakeRoomForSoteria.test.ts` finds every such page
 * and fails if one does not take this offset.
 */
export const SOTERIA_PANEL_RIGHT_CLASS =
  'right-[min(clamp(420px,32vw,540px),92vw)]';

/**
 * SoteriaSidePanel
 * ----------------
 * Right-side panel — docked, NOT modal. Opens via the Sparkles icon in
 * the top header. When it opens, the AuthLayout collapses the left
 * sidebar and squeezes the main content area so the page is still fully
 * visible alongside the assistant. No backdrop overlay. Behaves like
 * Claude's sidebar extension or ChatGPT's docked sidebar — the user can
 * keep working on the page while Soteria stays open.
 *
 * Architecture notes:
 *  - Wrapper stays mounted so the open/close transition animates. The
 *    INNER body remounts on every open (`{isOpen && <Body />}`) which:
 *      a) forces a fresh `useSoteriaChat` (and a fresh localStorage
 *         read) so the panel never shows stale state when the user has
 *         chatted on /soteria in between, and
 *      b) tears down voice resources (SpeechRecognition) on close
 *         instead of leaving them attached in the background.
 *  - Esc closes; X button closes. No click-outside-to-close because
 *    there is no backdrop and clicking the page should let the user
 *    interact with the page.
 *  - "Expand" (Maximize2) jumps to /soteria and closes the panel — the
 *    chat persists via localStorage so the full view continues the
 *    same conversation.
 */
export function SoteriaSidePanel() {
  const isOpen = useSoteriaSidePanelStore((s) => s.isOpen);
  const close = useSoteriaSidePanelStore((s) => s.close);
  const panelRef = useRef<HTMLElement>(null);

  // Portalled sheets must use the same available width as the page column.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const update = () => {
      document.documentElement.style.setProperty(
        '--workspace-overlay-right',
        `${panel.getBoundingClientRect().width}px`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(panel);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(
        '--workspace-overlay-right',
      );
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  return (
    <aside
      ref={panelRef}
      // role="complementary" (the default for <aside>) — NOT a dialog.
      // The panel is docked and the user can interact with the page
      // around it; aria-modal would lie to screen readers.
      aria-label="Soteria AI assistant"
      className={cn(
        // A FLEX SIBLING OF THE PAGE, not a `fixed` sheet beside it.
        //
        // It was `fixed right-0` with a matching `mr-[…]` on the content, two
        // constants that had to agree for the page to be pushed rather than
        // covered — and they did not. Observed on 10 Sept: with the drawer
        // open, a card's right border was missing and a long question read
        // "…stock exchange itself or O", clipped mid-word under the panel.
        //
        // A width in the flow cannot disagree with itself. The content column
        // beside this one carries `min-w-0`, which is what lets a long line
        // WRAP instead of pushing the row wider than the window — the other
        // half of why the text was running under here.
        // `relative` IS LOAD-BEARING, and leaving it out was a regression.
        // `ScrollToBottom` is `absolute bottom-28 left-1/2`, and its containing
        // block used to be this element while it was `fixed`. Nothing above it
        // is positioned — not `<main>`, not the row, not the width wrapper — so
        // without this the button resolved against the initial containing block
        // and floated over the PAGE, centred on the window. jsdom performs no
        // layout, so no test here can see it; `SoteriaAIPage` wraps the same
        // component in `relative` for exactly this reason.
        'relative flex shrink-0 flex-col overflow-hidden bg-card transition-[width] duration-300 ease-out motion-reduce:transition-none',
        // NO SHADOW. A docked column does not cast one onto the page beside
        // it; `shadow-2xl` is what made this look pasted on top of the app
        // rather than part of it. The border is the seam, at full strength
        // rather than the muted half — it is structure now, not decoration.
        // It goes with the width, or a closed panel leaves a hairline down
        // the right edge of the app.
        isOpen
          ? cn('border-l border-border', SOTERIA_PANEL_WIDTH_CLASS)
          : 'w-0',
      )}
    >
      {/* THE BODY KEEPS ITS OWN WIDTH while the shell animates OPEN, so the
          panel is revealed rather than squashed: without it every line inside
          re-wraps on each frame.
          
          On CLOSE it animates an empty box — `isOpen` unmounts the body at
          once. Deliberate rather than overlooked: keeping a live chat mounted
          through a 300ms exit means its queries, its scroll listener and its
          streaming subscription all outlive the click that dismissed it, to
          animate something nobody is looking at. */}
      <div className={cn('flex h-full flex-col', SOTERIA_PANEL_WIDTH_CLASS)}>
        {isOpen && <SoteriaSidePanelBody onClose={close} />}
      </div>
    </aside>
  );
}

interface SoteriaSidePanelBodyProps {
  onClose: () => void;
}

function SoteriaSidePanelBody({ onClose }: SoteriaSidePanelBodyProps) {
  const navigate = useNavigate();
  const kind = useSoteriaSidePanelStore((s) => s.kind);
  const isClient = kind === 'client';
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prependAnchorRef = useRef<number | null>(null);
  const {
    messages,
    streamingId,
    error,
    send,
    stop,
    newChat,
    switchChat,
    chats,
    historyLoading,
    activeChatId,
    hasMoreHistory,
    isLoadingOlder,
    loadOlderMessages,
    rateMessage,
    regenerateLastTurn,
  } = useSoteriaChat(kind);
  // The drawer sits next to whatever the advisor is reading, which is exactly
  // where dropping a statement onto the chat is the natural move — so it needs
  // the same attach affordances as the full page, not a trip through it.
  const uploads = useAttachmentUploads(activeChatId);

  // The drawer is ~420-540px, so a side-by-side chat list would leave the
  // conversation unreadable. Instead the body SWAPS between the conversation
  // and the history list (the pattern narrow chat apps use), which gives the
  // list the full width and keeps the drawer's job — answering without losing
  // your place on the page — intact.
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const historyGroups = useMemo(() => groupChats(chats), [chats]);
  const recents = useMemo(() => recentChats(chats), [chats]);
  const suggestions = usePromptSuggestions();
  // The greeting names the reader. `firstName` is optional on the profile, so
  // an account without one is greeted plainly rather than with a blank where a
  // name should be.
  const firstName = useUserStore((u) => u.user?.firstName)?.trim() || '';

  const openChat = (id: string) => {
    switchChat(id);
    setInput('');
    setView('chat');
  };

  // Anchor the viewport when older history prepends; otherwise pin to bottom.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && prependAnchorRef.current != null) {
      el.scrollTop = el.scrollHeight - prependAnchorRef.current;
      prependAnchorRef.current = null;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingId]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (
      !el ||
      !hasMoreHistory ||
      isLoadingOlder ||
      prependAnchorRef.current != null
    )
      return;
    if (el.scrollTop <= 80) {
      prependAnchorRef.current = el.scrollHeight - el.scrollTop;
      void loadOlderMessages().then((n) => {
        if (!n) prependAnchorRef.current = null;
      });
    }
  };

  const handleSend = async (override?: string) => {
    const text = (override ?? input).trim();
    // A file on its own is a message, so only a turn with neither is refused.
    if (!text && !uploads.pending.length) return;
    setInput('');
    const ready = await uploads.takeReady();
    void send(text, undefined, ready);
  };

  const handleExpand = () => {
    onClose();
    navigate({ to: '/soteria' });
  };

  const handleNewChat = () => {
    setInput('');
    newChat();
    setView('chat');
  };

  return (
    <>
      <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          {view === 'history' ? (
            <button
              type="button"
              onClick={() => setView('chat')}
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted"
              title="Back to the conversation"
              aria-label="Back to the conversation"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : (
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary shadow-sm">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </span>
          )}
          {/* NO TITLE ON THE CHAT, which is the reference's own choice rather
              than a saving. Its panel puts nothing in the bar but the
              controls; the greeting below carries the identity, where a reader
              is looking anyway. The bar had a title AND a subtitle — "Quick
              assistant" under "Soteria AI" — facing four icon buttons, so a
              32px strip held two lines of prose and four controls.
              
              A title survives on HISTORY: that view genuinely changes what the
              panel is showing, and the back arrow beside it needs something to
              name. */}
          {view === 'history' && (
            <p className="text-sm font-semibold text-foreground">Your chats</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() =>
              setView((v) => (v === 'history' ? 'chat' : 'history'))
            }
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-muted',
              view === 'history'
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground',
            )}
            title={
              view === 'history' ? 'Back to the conversation' : 'Your chats'
            }
            aria-label={
              view === 'history'
                ? 'Back to the conversation'
                : 'Show your chats'
            }
            aria-pressed={view === 'history'}
          >
            {view === 'history' ? (
              <MessageSquare className="h-4 w-4" />
            ) : (
              <History className="h-4 w-4" />
            )}
          </button>
          <button
            type="button"
            onClick={handleNewChat}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
            title="New chat"
            aria-label="Start a new chat"
          >
            <Plus className="h-4 w-4" />
          </button>
          {/* The full-page view is the financial assistant only; no client
              full view exists, so hide "expand" for the client assistant. */}
          {!isClient && (
            <button
              type="button"
              onClick={handleExpand}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              title="Open full view"
              aria-label="Open full Soteria view"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
            title="Close"
            aria-label="Close Soteria panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {view === 'history' ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {historyLoading && historyGroups.length === 0 ? (
            // Shimmer rows during the first fetch, so a populated history
            // doesn't flash the empty state on a cold open.
            <div className="space-y-2" aria-label="Loading chats">
              {['w-full', 'w-4/5', 'w-11/12', 'w-2/3', 'w-5/6', 'w-3/4'].map(
                (w, i) => (
                  <div
                    key={i}
                    className={`h-8 rounded-md bg-gradient-to-r from-muted via-muted-foreground/20 to-muted bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none ${w}`}
                  />
                ),
              )}
            </div>
          ) : historyGroups.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">
              No chats yet. Whatever you ask will show up here.
            </p>
          ) : (
            historyGroups.map((group) => (
              <div key={group.section} className="mb-4">
                <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {group.section}
                </p>
                {group.chats.map((chat) => (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => openChat(chat.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                      chat.id === activeChatId
                        ? 'bg-muted font-medium text-foreground'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-60" />
                    <span className="truncate">{chat.title}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      ) : (
        <>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="min-h-0 flex-1 overflow-y-auto px-5 py-3"
          >
            {isLoadingOlder && (
              <div className="py-2 text-center text-xs text-muted-foreground">
                Loading earlier messages…
              </div>
            )}
            {messages.length === 0 ? (
              <div className="flex h-full flex-col gap-1 px-1 pb-1">
                {/* THE GREETING SITS ON THE COMPOSER, NOT AT THE TOP.
              
                Two attempts before this one. It was `items-center
                justify-center` with `text-center`: an icon in a circle, a
                heading, a three-line paragraph and five pills, hovering in the
                vertical middle of a column some 800px tall. Then it was anchored
                to the TOP, which is the tidy answer and still the wrong one.
              
                A conversation grows UPWARD from the composer. An empty state
                that starts at the top leaves its content jumping the height of
                the panel the moment a first message lands; sitting just above
                the composer, the greeting is already where the first exchange
                will be, and the empty space is above it, where empty space
                belongs. The reference panel does exactly this.
              
                THE GREETING CARRIES THE IDENTITY, which is why the bar above has
                no title any more. It replaces a circled icon, a heading and a
                paragraph explaining what an assistant is to somebody who has
                just opened one. */}
                {/* `mt-auto` ON THE FIRST CHILD, not `justify-end` on the
                    column. They look the same until the content is taller than
                    the panel: a `justify-end` column overflows the TOP, and the
                    top of an `overflow-y-auto` box cannot be scrolled back to.
                    Roughly 490px of content here — a greeting, five openers and
                    five recents — so it fits an ordinary window and clips on a
                    short one. An auto top margin cannot have the problem: it
                    collapses to zero the moment there is no free space. */}
                <p className="mt-auto text-[22px] leading-tight font-normal text-primary">
                  {firstName ? `Hello, ${firstName}` : 'Hello'}
                </p>
                <p className="pb-5 text-[22px] leading-tight font-normal text-foreground">
                  {isClient
                    ? 'What can I do on this client?'
                    : 'How can I help you today?'}
                </p>
                {/* PILLS THAT HUG THEIR TEXT, stacked and left-aligned.
                
                Also two attempts wrong. They were a centred `flex-wrap`, which
                came out three and two and re-ragged itself as the window
                moved. Then they were full-width bordered rows with icons,
                which is a menu — five equal blocks reading as five equal
                obligations.
                
                `w-fit` is the whole difference: each opener is as wide as what
                it says, so a ragged edge cannot exist and the LENGTH of each
                suggestion becomes information. No icon and no border, for the
                same reason the reference has neither — these are things to
                say, not controls to operate.
                
                Openers for THIS page, so the assistant's reach is visible
                where the advisor already is (promptSuggestions.ts). */}
                <div className="flex flex-col items-start gap-2">
                  {suggestions.map(({ label, prompt }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => void handleSend(prompt)}
                      title={prompt}
                      className="w-fit max-w-full rounded-pill bg-muted px-4 py-2 text-left text-[13px] text-foreground transition-colors hover:bg-muted/70 hover:text-primary"
                    >
                      <span className="block truncate">{label}</span>
                    </button>
                  ))}
                </div>
                {/* An empty chat is exactly where a previous one is easiest to
                lose — surface the recent ones right here rather than making
                the advisor find the history toggle. */}
                {recents.length > 0 && (
                  // `text-left` left with the centring above it.
                  <div className="w-full pt-5">
                    <p className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Recent
                    </p>
                    {recents.map((chat) => (
                      <button
                        key={chat.id}
                        type="button"
                        onClick={() => openChat(chat.id)}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-muted-foreground transition-colors hover:bg-muted"
                      >
                        <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-60" />
                        <span className="truncate">{chat.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <SoteriaMessageList
                  messages={messages}
                  streamingMessageId={streamingId}
                  copiedId={null}
                  onCopy={(_id, content) => {
                    void navigator.clipboard.writeText(content);
                  }}
                  onRegenerate={() => {
                    void regenerateLastTurn();
                  }}
                  onRate={rateMessage}
                  onChoose={(value) => void send(value)}
                  sessionId={activeChatId}
                />
                <div ref={messagesEndRef} className="h-1" />
              </>
            )}
          </div>

          {/* Positioned against the <aside>, which carries `relative` for
              exactly this, and lifted clear of the composer. */}
          <ScrollToBottom containerRef={scrollRef} className="bottom-28" />
        </>
      )}

      {/* Composer belongs to the conversation, not the chat list. */}
      {view === 'chat' && (
        <div className="flex shrink-0 flex-col items-center gap-2 border-t border-border bg-card px-5 py-3">
          <SoteriaComposer
            value={input}
            onChange={setInput}
            onSubmit={() => void handleSend()}
            attachments={uploads.pending}
            onAttach={uploads.add}
            onRemoveAttachment={uploads.remove}
            placeholder="Ask Soteria…"
            maxWidthClass="max-w-none w-full"
            isStreaming={streamingId !== null}
            onStop={stop}
          />
          {error && (
            <div className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-[11px] text-destructive">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="w-full text-center text-[11px] text-muted-foreground">
            Soteria AI can make mistakes. Please verify responses.
          </div>
        </div>
      )}
    </>
  );
}
